import { useCallback, useEffect, useRef, useState } from 'react'

export const Video = ({ ...props }: React.VideoHTMLAttributes<HTMLVideoElement>) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  // state flags
  const [isPlaying, setIsPlaying] = useState(false)
  const [userPaused, setUserPaused] = useState(false)
  const [isInView, setIsInView] = useState(false)

  // 1. Observe whether video is in view
  useEffect(() => {
    if (!videoRef.current) return
    const observer = new IntersectionObserver(([entry]) => setIsInView(entry.isIntersecting), {
      threshold: 0.5,
    })
    observer.observe(videoRef.current)
    return () => {
      observer.disconnect()
    }
  }, [])

  // 2. Autoplay / pause based on in-view and userPaused
  useEffect(() => {
    const vid = videoRef.current
    if (!vid) return

    if (isInView && !userPaused) {
      vid
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => {
          /* might fail if autoplay blocked; ignore */
        })
    } else {
      vid.pause()
      setIsPlaying(false)
    }
  }, [isInView, userPaused])

  // 3. Toggle play/pause on click
  const handleToggle = useCallback(() => {
    const vid = videoRef.current
    if (!vid) return
    if (vid.paused) {
      vid
        .play()
        .then(() => {
          setIsPlaying(true)
          setUserPaused(false)
        })
        .catch(() => {})
    } else {
      vid.pause()
      setIsPlaying(false)
      setUserPaused(true)
    }
  }, [])

  return (
    <div className={'relative rounded-xl bg-black'}>
      <video
        ref={videoRef}
        muted={true}
        {...props}
        className="block max-w-full rounded-xl"
        onClick={handleToggle}
        // hide native controls since we have our own overlay
        controls={false}
      />

      {/* overlay play button when paused */}
      {!isPlaying && (
        <button
          onClick={handleToggle}
          aria-label="Play video"
          className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 transform cursor-pointer items-center justify-center border-none bg-transparent p-0"
        >
          <svg width="70" height="70" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="70" height="70" rx="35" fill="white" fillOpacity="0.9" />
            <path
              d="M25.0391 46.2956V23.7044C25.0391 22.8906 25.2398 22.2938 25.6413 21.9141C26.0428 21.5234 26.5202 21.3281 27.0736 21.3281C27.5618 21.3281 28.061 21.4692 28.571 21.7513L47.5326 32.8353C48.2053 33.2259 48.6719 33.5786 48.9323 33.8932C49.2036 34.197 49.3392 34.566 49.3392 35C49.3392 35.4232 49.2036 35.7921 48.9323 36.1068C48.6719 36.4214 48.2053 36.7741 47.5326 37.1647L28.571 48.2487C28.061 48.5308 27.5618 48.6719 27.0736 48.6719C26.5202 48.6719 26.0428 48.4766 25.6413 48.0859C25.2398 47.6953 25.0391 47.0985 25.0391 46.2956Z"
              fill="black"
              fillOpacity="0.9"
            />
          </svg>
        </button>
      )}
    </div>
  )
}
